import React, { ReactElement, useContext, useState, useRef, memo } from "react";
import { FileContext } from "../../store/FileContextProvider";
import { LoaderContext } from "../../store/LoaderContextProvider";
import { actionWithSpinnerLoading } from "../../utils/loaderUtils";
import { ScannerContext } from "../../store/ScannerContextProvider";
import FileDropContainerInfoCard from "./components/FileDropContainerInfoCard";
import * as Styles from "./FileDropContainer.styles";

interface FileDropContainerPropsType {
  children: ReactElement;
  onFileDropped?: () => void;
  onDropNavigate?: () => void;
}

function FileDropContainer({ children, onFileDropped, onDropNavigate }: FileDropContainerPropsType) {
  const { handleXmlFileImport, handleXmlFileImportWithScannerCheck } = useContext(FileContext);
  const { isScanOn, startScan, scannerArray } = useContext(ScannerContext);
  const { setIsloading } = useContext(LoaderContext);

  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const dragEventCounter = useRef(0);

  const dropActionsMillisecondsDuration: number = 1000;

  function handleDragEnter(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    dragEventCounter.current += 1;
    setIsDraggingFile(true);
  }

  function handleDragLeave(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    dragEventCounter.current -= 1;
    if (dragEventCounter.current === 0) setIsDraggingFile(false);
  }

  function handleDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    dragEventCounter.current = 0;
    setIsDraggingFile(false);
    const file = event.dataTransfer.files[0];
    if (!file) return;

    if (onDropNavigate) {
      handleXmlFileImport(file);
      if (!isScanOn) startScan();
      if (onFileDropped) onFileDropped();
      setTimeout(() => {
        onDropNavigate();
      }, dropActionsMillisecondsDuration - 100);
    }

    actionWithSpinnerLoading({
      handleAction: () => {
        handleXmlFileImportWithScannerCheck(file, scannerArray);
        if (!isScanOn) startScan();
      },
      setIsLoading: setIsloading,
      duration: dropActionsMillisecondsDuration,
      handleFocus: () => {
        if (onFileDropped) onFileDropped();
      },
    });
  }

  return (
    <Styles.Container
      onDragEnter={handleDragEnter}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}
      {isDraggingFile && <FileDropContainerInfoCard />}
    </Styles.Container>
  );
}

export default memo(FileDropContainer);
